import { Injectable, inject } from "@angular/core";
import { saveAs } from "file-saver";
import { ISignRequest, ISigner } from "@modals/api.modal";
import { AppService } from "./app.service";

/** Minimum / maximum Excel column width in characters so exports are readable. */
const COL_W_MIN = 14;
const COL_W_MAX = 72;
const COL_W_PADDING = 4;

/**
 * Builds .xlsx files from loaded sign-request data for offline reporting / audit trails.
 * Uses write-excel-file (fflate only) — no deprecated npm sub-deps or xlsx audit advisories.
 */
@Injectable({
  providedIn: "root",
})
export class RequestReportExportService {
  private readonly _app = inject(AppService);

  private isoFileStamp(): string {
    return new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
  }

  /** Spreadsheet-safe text (formula-injection mitigation). */
  private safeCell(value: unknown): string {
    if (value === null || value === undefined) return "";
    const s = String(value);
    if (/^[=+\-@\t\r]/.test(s)) return `'${s}`;
    return s;
  }

  private sortedSigners(req: ISignRequest): ISigner[] {
    return [...(req.Signers ?? [])].sort(
      (a, b) => (a.Order ?? 0) - (b.Order ?? 0)
    );
  }

  private async downloadSheet(
    sheetName: string,
    rows: ReadonlyArray<Record<string, string>>,
    fileName: string
  ): Promise<void> {
    const writeExcelFile = (await import("write-excel-file/browser")).default;
    const keys = Object.keys(rows[0]);
    const headerRow = keys.map((key) => ({
      value: key,
      fontWeight: "bold" as const,
    }));
    const dataRows = rows.map((row) =>
      keys.map((key) => ({ value: row[key] ?? "" }))
    );
    const columns = keys.map((key) => {
      let maxLen = key.length;
      for (const row of rows) {
        const len = String(row[key] ?? "").length;
        if (len > maxLen) maxLen = len;
      }
      return {
        width: Math.min(
          Math.max(maxLen + COL_W_PADDING, COL_W_MIN),
          COL_W_MAX
        ),
      };
    });

    const blob = await writeExcelFile([headerRow, ...dataRows], {
      sheet: sheetName,
      columns,
      stickyRowsCount: 1,
    }).toBlob();
    saveAs(blob, fileName);
  }

  /**
   * One row per request — matches main portal grid fields (reporting snapshot).
   */
  async exportRawRowsToExcel(rows: Record<string, unknown>[]): Promise<boolean> {
    if (!rows.length) return false;
    const keyOrder: string[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (!seen.has(key)) {
          seen.add(key);
          keyOrder.push(key);
        }
      }
    }
    const sheetRows = rows.map((row) => {
      const out: Record<string, string> = {};
      for (const key of keyOrder) {
        out[key] = this.safeCell(row[key]);
      }
      return out;
    });
    await this.downloadSheet(
      "Requests",
      sheetRows,
      `requests-export-${this.isoFileStamp()}.xlsx`
    );
    return true;
  }

  async exportRequestsToExcel(requests: ISignRequest[]): Promise<boolean> {
    if (!requests.length) return false;
    const rows = requests.map((r) => ({
      RequestNo: this.safeCell(r.RequestNo),
      SignRequestId: this.safeCell(r.SignRequestId),
      CustomerName: this.safeCell(r.CustomerName),
      AppName: this.safeCell(r.AppName),
      RequesterName: this.safeCell(r.RequesterName),
      RequesterEmail: this.safeCell(r.RequesterEmail),
      RequestDate: this.safeCell(this._app.getFormattedDate(r.RequestDate)),
      RequestStatus: this.safeCell(r.RequestStatus),
      DocName: this.safeCell(r.DocName),
      SignersCount: this.safeCell((r.Signers ?? []).length),
    }));
    await this.downloadSheet(
      "Requests",
      rows,
      `requests-export-${this.isoFileStamp()}.xlsx`
    );
    return true;
  }

  /**
   * Audit-style trail: one row per signer slot with sign/pending timestamps.
   */
  async exportAuditTrailToExcel(requests: ISignRequest[]): Promise<boolean> {
    if (!requests.length) return false;
    type AuditRow = {
      RequestNo: string;
      SignRequestId: string;
      CustomerName: string;
      RequestStatus: string;
      RequestDate: string;
      RequesterName: string;
      RequesterEmail: string;
      DocName: string;
      SignerOrder: string;
      SignerName: string;
      SignerEmail: string;
      SignDate: string;
      SignActivity: string;
    };
    const rows: AuditRow[] = [];
    for (const req of requests) {
      const signers = this.sortedSigners(req);
      if (!signers.length) {
        rows.push({
          RequestNo: this.safeCell(req.RequestNo),
          SignRequestId: this.safeCell(req.SignRequestId),
          CustomerName: this.safeCell(req.CustomerName),
          RequestStatus: this.safeCell(req.RequestStatus),
          RequestDate: this.safeCell(this._app.getFormattedDate(req.RequestDate)),
          RequesterName: this.safeCell(req.RequesterName),
          RequesterEmail: this.safeCell(req.RequesterEmail),
          DocName: this.safeCell(req.DocName),
          SignerOrder: "",
          SignerName: "",
          SignerEmail: "",
          SignDate: "",
          SignActivity: "No_signers_defined",
        });
        continue;
      }
      for (const s of signers) {
        const signed = !!s.SignDate;
        rows.push({
          RequestNo: this.safeCell(req.RequestNo),
          SignRequestId: this.safeCell(req.SignRequestId),
          CustomerName: this.safeCell(req.CustomerName),
          RequestStatus: this.safeCell(req.RequestStatus),
          RequestDate: this.safeCell(this._app.getFormattedDate(req.RequestDate)),
          RequesterName: this.safeCell(req.RequesterName),
          RequesterEmail: this.safeCell(req.RequesterEmail),
          DocName: this.safeCell(req.DocName),
          SignerOrder: this.safeCell(s.Order),
          SignerName: this.safeCell(s.SignerName),
          SignerEmail: this.safeCell(s.SignerEmail),
          SignDate: signed
            ? this.safeCell(
                this._app.getFormattedDate(
                  s.SignDate instanceof Date
                    ? s.SignDate.toISOString()
                    : String(s.SignDate ?? "")
                )
              )
            : "",
          SignActivity: signed ? "Signed" : "Pending_signature",
        });
      }
    }
    await this.downloadSheet(
      "Signer_audit_trail",
      rows,
      `audit-report-${this.isoFileStamp()}.xlsx`
    );
    return true;
  }
}
